import { useState, useMemo } from "react";
import { useChangeTaskLevel, useGetDoTasks, useGetMitTasks } from "../../Controllers/TaskManagementController";
import Layout from "../../Layout/Layout"
import { toast } from "react-toastify";
import TaskNotesModal from "../Modals/TaskNotesModal";
import TaskSubtasksModal from "../Modals/TaskSubtasksModal";
import { useQueryClient } from "@tanstack/react-query";
import parse from "html-react-parser";

const DashboardTaskManagement = () => {
    const { data: mitData, isLoading: mitListLoading, refetch: mitRefetch } = useGetMitTasks();
    const { data: doListData, isLoading: doListLoading, refetch: doListRefetch } = useGetDoTasks();
    const { mutate: taskLevelMutation, isPending: taskLevelChangePending } = useChangeTaskLevel();

    const queryClient = useQueryClient();

    const [activeTab, setActiveTab] = useState("mit"); // "mit" | "do"
    const [pendingTaskId, setPendingTaskId] = useState(null);

    const [showSubtaskModal, setShowSubtaskModal] = useState(false);
    const [subtasks, setSubtasks] = useState([]);
    const [taskId, setTaskId] = useState(null);

    const [showTaskNotesModal, setShowTaskNotesModal] = useState(false);
    const [taskNotes, setTaskNotes] = useState("");

    const isLoading = doListLoading || mitListLoading;

    const flatMit = useMemo(() => {
        const container = Array.isArray(mitData?.data) ? mitData.data : Array.isArray(mitData) ? mitData : [];
        return Array.isArray(container[0]) ? container[0] : container;
    }, [mitData]);

    const flatDo = useMemo(() => {
        const container = Array.isArray(doListData?.data) ? doListData.data : Array.isArray(doListData) ? doListData : [];
        return Array.isArray(container[0]) ? container[0] : container;
    }, [doListData]);



    const getPendingSubtasks = (subTasks) => {
        if (subTasks.length > 0) {
            return subTasks.filter(sub => sub.status === "pending");
        }
        return [];
    }

    const handleOpenNotesModal = (note) => {
        setTaskNotes(note || "");
        setShowTaskNotesModal(true);
    };

    const handleCloseNotesModal = () => {
        setShowTaskNotesModal(false);
        setTaskNotes("");
    };

    const handleOpenSubtasksModal = (subtasks, taskId) => {
        setSubtasks(subtasks);
        setShowSubtaskModal(true);
        setTaskId(taskId);
    }

    const handleCloseSubtasksModal = () => {
        setShowSubtaskModal(false);
        setSubtasks([]);
        setTaskId(null);
    }

    const handleToggle = (listType, task) => {
        const pendingSubtasks = getPendingSubtasks(task?.sub_tasks);

        if (pendingSubtasks.length > 0) {

            return handleOpenSubtasksModal(pendingSubtasks, task.id);
        }


        if (!task?.id || pendingTaskId === task.id) return;
        setPendingTaskId(task.id);


        let payload = { level: null, completion_status: "" };

        if (listType === "do") {
            const isDone = (task?.completion_status || "").toLowerCase() === "done";
            payload = isDone
                ? { level: "Do List", completion_status: "", taskId: task.id }
                : { level: null, completion_status: "Done", taskId: task.id };
        } else {
            const isAchieved = (task?.completion_status || "").toLowerCase() === "achieved";
            payload = isAchieved
                ? { level: "MIT List", completion_status: "", taskId: task.id }
                : { level: null, completion_status: "Achieved", taskId: task.id };
        }

        taskLevelMutation(payload, {
            onSuccess: () => {
                setPendingTaskId(null);
                queryClient.invalidateQueries({ queryKey: ["dashboard", "levelDetails"] });
                mitRefetch?.();
                doListRefetch?.();
                toast.info("Task status changed successfully");
            },
            onError: () => {
                setPendingTaskId(null);
                toast.error("Unable to update task status");
            },
        })
    };

    if (isLoading) {
        return (
            <div className="col-lg-12 col-xl-6">
                <div className="tasks-card dashboard-habit-card">
                    <h3 className="h6 mb-3">My Tasks</h3>
                    <div className="table-responsive">
                        <table className="table dash-cutom-table">
                            <thead>
                                <tr className="text-body-b2 table-head-user" style={{ fontWeight: "400" }}>
                                    <th>Task</th>
                                    <th>Notes</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[...Array(4)].map((_, idx) => (
                                    <tr key={idx}>
                                        <td>
                                            <div className="placeholder-glow">
                                                <span className="placeholder col-8"></span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="placeholder-glow">
                                                <span className="placeholder col-4"></span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="placeholder-glow">
                                                <span className="placeholder col-3"></span>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="col-lg-12 col-xl-6">
            <div className="tasks-card dashboard-habit-card">
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <h3 className="h6 mb-0">My Tasks</h3>
                    <div className="btn-group" role="group" aria-label="Task tabs">
                        <button
                            type="button"
                            className={`btn btn-sm ${activeTab === "mit" ? "memory-button" : "btn-outline-primary"}`}
                            onClick={() => setActiveTab("mit")}
                            style={{
                                borderRadius: "30px",
                                marginRight: "10px"
                            }}
                        >
                            MIT List
                        </button>
                        <button
                            type="button"
                            className={`btn btn-sm ${activeTab === "do" ? "memory-button" : "btn-outline-primary"}`}
                            onClick={() => setActiveTab("do")}
                            style={{
                                borderRadius: "30px",
                                marginRight: "10px"
                            }}
                        >
                            Do List
                        </button>
                    </div>
                </div>

                {activeTab === "mit" ? (
                    <>
                        {(!Array.isArray(flatMit) || flatMit.length === 0) ? (
                            <p className="text-mute mb-4">You haven't set your mandatory mission</p>
                        ) : (
                            <div className="table-responsive">
                                <table className="table dash-cutom-table">
                                    <thead>
                                        <tr className="text-body-b2 table-head-user" style={{ fontWeight: "400" }}>
                                            <th>Task</th>
                                            <th>Notes</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-body-b2">
                                        {flatMit.map((task, index) => {
                                            const isAchieved = (task?.completion_status || "").toLowerCase() === "achieved";
                                            const disabled = pendingTaskId === task.id;
                                            return (
                                                <tr key={index}>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <span className="text-white">
                                                                {parse(task?.title || "")}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <button
                                                            style={{
                                                                backgroundColor: "transparent",
                                                                border: "none",
                                                                padding: "0",
                                                                color: "#008cff",
                                                                textDecoration: "underline"
                                                            }}
                                                            onClick={() => handleOpenNotesModal(task?.notes)}
                                                        >
                                                            View Notes
                                                        </button>
                                                    </td>
                                                    <td>
                                                        <span
                                                            role="button"
                                                            tabIndex={0}
                                                            onClick={() => !disabled && handleToggle("mit", task)}
                                                            className={`status-badge-view ${isAchieved ? "status-achieved" : "status-undo"}`}
                                                            style={{ cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.6 : 1 }}
                                                        >
                                                            {disabled ? "Updating..." : isAchieved ? "Achieved" : "Not Achieved"}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        {(!Array.isArray(flatDo) || flatDo.length === 0) ? (
                            <p className="text-mute mb-4">No Do List tasks scheduled</p>
                        ) : (
                            <div className="table-responsive">
                                <table className="table dash-cutom-table">
                                    <thead>
                                        <tr className="text-body-b2 table-head-user" style={{ fontWeight: "400" }}>
                                            <th>Task</th>
                                            <th>Notes</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-body-b2">
                                        {flatDo.map((task, index) => {
                                            const isDone = (task?.completion_status || "").toLowerCase() === "done";
                                            const disabled = pendingTaskId === task.id;
                                            return (
                                                <tr key={index}>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <span className="text-white">
                                                                {parse(task?.title || "")}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <button
                                                            // className="task-notes btn btn-link p-0"
                                                            style={{
                                                                backgroundColor: "transparent",
                                                                border: "none",
                                                                padding: "0",
                                                                color: "#008cff",
                                                                textDecoration: "underline"
                                                            }}
                                                            onClick={() => handleOpenNotesModal(task?.notes)}
                                                        >
                                                            View Notes
                                                        </button>
                                                    </td>
                                                    <td>
                                                        <span
                                                            role="button"
                                                            tabIndex={0}
                                                            onClick={() => !disabled && handleToggle("do", task)}
                                                            className={`status-badge-view ${isDone ? "status-achieved" : "status-undo"}`}
                                                            style={{ cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.6 : 1 }}
                                                        >
                                                            {disabled ? "Updating..." : isDone ? "Completed" : "Not Completed"}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </div>
            <TaskNotesModal
                modalNote={taskNotes}
                onClose={handleCloseNotesModal}
                open={showTaskNotesModal}
            />
            <TaskSubtasksModal
                subtasks={subtasks}
                onClose={handleCloseSubtasksModal}
                open={showSubtaskModal}
                taskId={taskId}
            />
        </div>
    )
}

export default DashboardTaskManagement;